import { portalApi } from "@services/api/portalApi";
import { graphApi } from "@services/api/graphApi";
import { StaffOid, StudentOid, TeamsMessage } from "@godalming/api-types";

export async function createTeamsChat(recipient: string, sender: string) {
    try {
        const response = await graphApi.post('/chats', {
            "chatType": "oneOnOne",
            "members": [
                {
                    "@odata.type": "#microsoft.graph.aadUserConversationMember",
                    "roles": ["owner"],
                    "user@odata.bind": `https://graph.microsoft.com/v1.0/users('${sender}')`
                },
                {
                    "@odata.type": "#microsoft.graph.aadUserConversationMember",
                    "roles": ["owner"],
                    "user@odata.bind": `https://graph.microsoft.com/v1.0/users('${recipient}')`
                }
            ]
        });
        return response.data.id;
    } catch (error) {
        console.error('Error creating Teams chat:', error);
        throw error;
    }
}

export async function sendMessageToChat(chatId: string, content: any) {
    try {
        await graphApi.post(`/chats/${chatId}/messages`, {
            "body": {
                "contentType": "html",
                "content": content,
            }
        });
    } catch (error) {
        console.error('Error sending message to Teams chat:', error);
        throw error;
    }
}

export async function createAndSendMessage(recipient: string, content: any, sender: string) {
    try {
        const chatId = await createTeamsChat(recipient, sender);
        await sendMessageToChat(chatId, content);
    } catch (error) {
        console.error('Error creating chat and sending message:', error);
        throw error;
    }
}

export async function createAndSendMultipleMessages(recipients: string[], content: any, sender: string) {
    const results = await Promise.allSettled(
        recipients.map((recipient) => createAndSendMessage(recipient, content, sender)),
    );

    const failures = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (failures.length > 0) {
        throw new Error(`Failed to send ${failures.length} of ${recipients.length} messages.`);
    }
}

export async function getAllStaffOids() {
    try {
        const response = await portalApi.get<StaffOid[]>('/directory/staff/communication-recipients');
        return response.data;
    } catch (error) {
        console.error('Error fetching staff OIDs:', error);
        throw error;
    }
}

export async function getAllStudentOids() {
    const response = await portalApi.get<StudentOid[]>('/directory/students/communication-recipients');
    return response.data;
}

export async function sendTeamsMessage(request: TeamsMessage) {
    const sender = (await graphApi.get<{ id: string }>('/me?$select=id')).data.id;
    const recipients = [
        ...request.studentRecipients.map((studentNumber) => `${studentNumber}@godalming.ac.uk`),
        ...request.staffRecipients,
    ];

    if (request.type === 'oneOnOne') {
        await createAndSendMultipleMessages(recipients, request.message, sender);
        return;
    }

    const members = [sender, ...recipients].map((recipient) => ({
        '@odata.type': '#microsoft.graph.aadUserConversationMember',
        roles: ['owner'],
        'user@odata.bind': `https://graph.microsoft.com/v1.0/users('${recipient}')`,
    }));
    const chat = await graphApi.post<{ id: string }>('/chats', {
        chatType: 'group',
        topic: request.topic || undefined,
        members,
    });
    await sendMessageToChat(chat.data.id, request.message);
}
